// D:\directiveai-react\frontend\src\components\home\HomeInput\HomeInput.js (modificado)

import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import { 
  useMediaQuery, 
  useTheme, 
  Menu, 
  MenuItem, 
  Box,
  ClickAwayListener,
} from '@mui/material';
import { 
  DraftingCompass, 
  DollarSign, 
  Video, 
  Book, 
  Music, 
  ShoppingCart, 
  Briefcase, 
  Camera, 
  Smartphone, 
  Heart, 
  Zap, 
  Calendar, 
  Users, 
  Palette, 
  Gift, 
  HelpCircle,
  Monitor,
  Mic,
  Upload,
  Search,
  MessageCircleDashed
} from 'lucide-react';
import debounce from 'lodash/debounce';
import { getSuggestions, TrendingSuggestions } from '../data/suggestions';
import AGTHallService from '../services/AGTHallService';
import {
  CenteredContainer,
  StyledFooter,
  LinksContainer,
  FooterLink,
  HelpButton,
} from './HomeInputStyles';
import AppleSpinner from '../../common/AppleSpinner';

// Importar el sistema de estilos factorizado
import { INTERFACE_STYLES, getStyleComponent } from './styles';

// Importar hook de traducción
import { useTranslation } from 'react-i18next';

// Importar el nuevo componente CustomizeModal
import CustomizeModal from '../CustomizeModal/CustomizeModal';

// Simplificado para eliminar las frases de espera
const useWaitingState = () => {
  // Mantener la estructura básica del hook pero sin funcionalidad de mensajes
  const stopWaiting = useCallback(() => {
    // No hacer nada, solo mantener la interfaz para compatibilidad
  }, []);

  const startWaiting = useCallback(() => {
    // No hacer nada, solo mantener la interfaz para compatibilidad
  }, []);

  return {
    waitingMessage: '', // Siempre devolver cadena vacía
    startWaiting,
    stopWaiting
  };
};

const useInputState = (isWaiting) => {
  const [state, setState] = useState({
    input: '',
    showTrending: false,
    showSuggestions: false,
    filteredSuggestions: [],
    isInitialFocus: true,
  });

  const updateState = useCallback((updates) => {
    setState(prev => ({ ...prev, ...updates }));
  }, []);

  const debouncedUpdateSuggestions = useMemo(
    () => debounce((value) => {
      if (value) {
        const suggestions = getSuggestions(value);
        updateState({
          filteredSuggestions: suggestions,
          showSuggestions: true,
          showTrending: false,
        });
      } else {
        updateState({
          filteredSuggestions: [],
          showSuggestions: false,
          showTrending: false, // Cambiado de true a false para no mostrar tendencias al limpiar
        });
      }
    }, 200),
    [updateState]
  );

  useEffect(() => {
    return () => {
      debouncedUpdateSuggestions.cancel();
    };
  }, [debouncedUpdateSuggestions]);

  return {
    state,
    updateState,
    debouncedUpdateSuggestions,
  };
};

const FooterLinks = React.memo(() => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { t } = useTranslation(); // Añadir hook de traducción
  
  const links = isMobile ? [
  ] : [
    // Show all links on desktop
    { text: t('footer.pro'), url: 'https://directiveai.org/agt' },
    { text: t('footer.ups'), url: 'https://directiveai.org/ups-overview' },
    { text: t('footer.directiveAI'), url: 'https://directiveai.org/about' },
    { text: t('footer.simplicity'), url: 'https://directiveai.org/integration-guide' }
  ];

  return (
    <StyledFooter>
      <LinksContainer>
        {links.map((link) => (
          <FooterLink 
            key={link.text}
            href={link.url} 
            target="_blank" 
            rel="noopener"
          >
            {link.text}
          </FooterLink>
        ))}
      </LinksContainer>
      <HelpMenu />
    </StyledFooter>
  );
});

const HelpMenu = React.memo(() => {
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const theme = useTheme();
  const { t } = useTranslation();

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleIntercomOpen = () => {
    if (typeof window !== 'undefined' && window.Intercom) {
      window.Intercom('show');
    }
    handleClose();
  };

  const helpLinks = [
    { text: t('help.faq'), url: 'https://directiveai.org/knowledge-base', isIntercom: false, icon: <HelpCircle size={14} /> },
    { text: t('help.integration'), url: 'https://directiveai.org/docs', isIntercom: false, icon: <Book size={14} /> },
    { text: t('help.support'), url: null, isIntercom: true, icon: <MessageCircleDashed size={14} /> },
  ];

  return (
    <>
      <HelpButton
        aria-label="help"
        onClick={handleClick}
      >
        <HelpCircle />
      </HelpButton>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        onClick={(e) => e.stopPropagation()}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
        PaperProps={{
          sx: {
            borderRadius: '10px',
            boxShadow: theme.palette.mode === 'dark' 
              ? '0 5px 15px rgba(0, 0, 0, 0.3)' 
              : '0 5px 15px rgba(0, 0, 0, 0.1)',
            border: theme.palette.mode === 'dark'
              ? '1px solid rgba(255, 255, 255, 0.1)'
              : '1px solid rgba(0, 0, 0, 0.05)',
            padding: '4px',
            minWidth: '150px',
            marginTop: '8px',
          }
        }}
      >
        {helpLinks.map((link) => (
          <MenuItem
            key={link.text}
            onClick={() => {
              if (link.isIntercom) {
                handleIntercomOpen();
              } else {
                window.open(link.url, '_blank');
                handleClose();
              }
            }}
            dense
            sx={{ borderRadius: '6px' }}
          >
            <Box display="flex" alignItems="center">
              {React.cloneElement(link.icon, { style: { marginRight: 8 } })}
              {link.text}
            </Box>
          </MenuItem>
        ))}
      </Menu>
    </>
  );
});

const HomeInput = ({ onSendMessage, onOpenAGTPromo, isPro = false, userId, selectedApps = ['home', 'chat'] }) => {
  const [isWaiting, setIsWaiting] = useState(false);
  const [selectedCard, setSelectedCard] = useState(null);
  const inputRef = useRef(null);
  const theme = useTheme();
  const [customizeDialogOpen, setCustomizeDialogOpen] = useState(false);
  const [interfaceStyle, setInterfaceStyle] = useState(INTERFACE_STYLES.MINIMAL);
  const [isLoadingStyle, setIsLoadingStyle] = useState(true);
  const { t } = useTranslation(); // Añadir hook de traducción

  const { waitingMessage, startWaiting } = useWaitingState();
  const { state, updateState, debouncedUpdateSuggestions } = useInputState(isWaiting);

  // Usar traducciones en los cards
  const AGT_CARDS = useMemo(() => [
    { 
      Icon: DraftingCompass, 
      text: t('homeInput.cards.edu'), 
      action: "edu",
      isProOnly: false
    },
    { 
      Icon: DollarSign, 
      text: t('homeInput.cards.ups'), 
      action: "ups",
      isProOnly: false
    }
  ], [t]);

  // Usar traducciones en los prompt cards
  const PROMPT_CARDS = useMemo(() => [
    { 
      Icon: typeof window !== 'undefined' && window.innerWidth > 768 ? Monitor : Camera, 
      text: typeof window !== 'undefined' && window.innerWidth > 768 ? t('appPrompts.shareScreen') : t('appPrompts.shareCamera'), 
      action: "vision",
      isProOnly: true
    },
    { 
      Icon: Search, 
      text: t('appPrompts.searchWeb'), 
      action: "search",
      isProOnly: false
    },
    { 
      Icon: Mic, 
      text: t('appPrompts.voiceRecording'), 
      action: "voice",
      isProOnly: true
    },
    { 
      Icon: Upload, 
      text: t('appPrompts.uploadFiles'), 
      action: "files",
      isProOnly: false
    },
    { 
      Icon: Video, 
      text: t('prompts.videos'), 
      action: "videos",
      isProOnly: false
    },
    { 
      Icon: Music, 
      text: t('prompts.music'), 
      action: "music",
      isProOnly: false
    },
    { 
      Icon: Search, 
      text: t('prompts.headlines'), 
      action: "headlines",
      isProOnly: false
    },
    { 
      Icon: Calendar, 
      text: t('prompts.events'), 
      action: "events", 
      isProOnly: false
    },
    { 
      Icon: Zap, 
      text: t('prompts.productivity'), 
      action: "productivity",
      isProOnly: false
    },
    { 
      Icon: Book, 
      text: t('prompts.books'), 
      action: "books",
      isProOnly: false
    },
    { 
      Icon: ShoppingCart, 
      text: t('prompts.ecommerce'), 
      action: "ecommerce",
      isProOnly: false
    },
    { 
      Icon: Users, 
      text: t('prompts.communities'), 
      action: "communities",
      isProOnly: false
    },
    { 
      Icon: Briefcase, 
      text: t('prompts.jobs'), 
      action: "jobs",
      isProOnly: false
    },
    { 
      Icon: Palette, 
      text: t('prompts.design'), 
      action: "design",
      isProOnly: false
    },
    { 
      Icon: Heart, 
      text: t('prompts.topics'), 
      action: "topics",
      isProOnly: false
    },
    { 
      Icon: Gift, 
      text: t('prompts.products'), 
      action: "products",
      isProOnly: false
    },
    { 
      Icon: Smartphone, 
      text: t('prompts.apps'), 
      action: "apps",
      isProOnly: false
    }
  ], [t]);

  const randomPromptCards = useMemo(() => 
    [...PROMPT_CARDS].sort(() => 0.5 - Math.random()).slice(0, 2),
    [PROMPT_CARDS]
  );

  // Cargar el estilo de interfaz desde la API al iniciar
  useEffect(() => {
    const loadUserInterfaceStyle = async () => {
      setIsLoadingStyle(true);
      try {
        if (userId) {
          try {
            const savedStyle = await AGTHallService.getUserInterfaceStyle(userId);
            // Verificar si el estilo guardado es válido
            if (Object.values(INTERFACE_STYLES).includes(savedStyle)) {
              setInterfaceStyle(savedStyle);
            } else {
              // Para compatibilidad con versiones anteriores, si es necesario
              let compatStyle = savedStyle;
              
              // Solo si realmente necesitamos mapear estilos antiguos
              if (savedStyle === 'classic') compatStyle = INTERFACE_STYLES.MINIMAL;
              else if (savedStyle === 'modern') compatStyle = INTERFACE_STYLES.ASSISTANT;
              
              // Si después del mapeo el estilo es válido, usarlo
              if (Object.values(INTERFACE_STYLES).includes(compatStyle)) {
                setInterfaceStyle(compatStyle);
                // Actualizar en la base de datos
                await AGTHallService.setUserInterfaceStyle(userId, compatStyle);
              } else {
                // Si no es válido incluso después del mapeo, usar el predeterminado
                setInterfaceStyle(INTERFACE_STYLES.MINIMAL);
                await AGTHallService.setUserInterfaceStyle(userId, INTERFACE_STYLES.MINIMAL);
              }
            }
          } catch (error) {
            console.error('[FRONTEND] Error loading style from API:', error);
            // Si hay error al cargar, usamos el estilo predeterminado
            setInterfaceStyle(INTERFACE_STYLES.MINIMAL);
          }
        } else {
          setInterfaceStyle(INTERFACE_STYLES.MINIMAL);
        }
      } catch (error) {
        console.error('[FRONTEND] Critical error in style loading:', error);
        setInterfaceStyle(INTERFACE_STYLES.MINIMAL);
      } finally {
        setIsLoadingStyle(false);
      }
    };
    
    loadUserInterfaceStyle();
  }, [userId]);

  // MODIFICADO: Actualizado handleCardClick para manejar tanto cards como sugerencias AGT
  const handleCardClick = useCallback((textOrAction) => {
    setSelectedCard(textOrAction);
    
    // Verificar si es una acción de AGT específica
    if (textOrAction === 'agt_ups' || textOrAction === 'agt_health' || textOrAction === 'agt_fit') {
      // Mapear las acciones a las secciones correspondientes
      const actionToSectionMap = {
        'agt_ups': 'ups',
        'agt_health': 'health', 
        'agt_fit': 'fit'
      };
      
      const targetSection = actionToSectionMap[textOrAction];
      
      if (targetSection) {
        // Disparar evento personalizado para navegar directamente
        const navigateEvent = new CustomEvent('navigateToSection', { 
          detail: { section: targetSection } 
        });
        window.dispatchEvent(navigateEvent);
        
        // También actualizar localStorage
        localStorage.setItem('activeSection', targetSection);
        
        // Activar la visibilidad en navegación si es necesario
        if (targetSection === 'ups') {
          localStorage.setItem('showUpsInNav', 'true');
        } else if (targetSection === 'health') {
          localStorage.setItem('showHealthInNav', 'true');
        } else if (targetSection === 'fit') {
          localStorage.setItem('showFitInNav', 'true');
        }
        
        return; // Salir temprano para evitar el resto de la lógica
      }
    }
    
    // Resto de la lógica existente para otros casos
    if (textOrAction === t('homeInput.cards.ups') || textOrAction === t('homeInput.cards.edu')) {
      onOpenAGTPromo();
    } else if (textOrAction === t('homeInput.cards.chat')) {
      // Usar un evento personalizado para comunicarse con FullDashboard
      const navigateEvent = new CustomEvent('navigateToSection', { 
        detail: { section: 'chat' } 
      });
      window.dispatchEvent(navigateEvent);
      
      // También actualizar localStorage para mantener consistencia
      localStorage.setItem('activeSection', 'chat');
    } else if (textOrAction === t('homeInput.cards.support')) {
      // Abrir Intercom
      if (typeof window !== 'undefined' && window.Intercom) {
        window.Intercom('show');
      }
    } else {
      // CAMBIO IMPORTANTE: Solo colocar el texto en el input, no enviar mensaje
      updateState({ 
        input: textOrAction, 
        showTrending: false, 
        showSuggestions: false 
      });
      
      // Enfocar el input para mejor experiencia de usuario
      if (inputRef.current) {
        inputRef.current.focus();
      }
    }
  }, [onOpenAGTPromo, updateState, t]);

  const handleCustomizeClick = useCallback(() => {
    setCustomizeDialogOpen(true);
  }, []);

  const handleCloseCustomizeDialog = useCallback(() => {
    setCustomizeDialogOpen(false);
  }, []);

  const handleStyleChange = useCallback(async (newStyle) => {
    setInterfaceStyle(newStyle);
   
    // Save to API if there's a valid userId
    if (userId) {
      try {
        // Don't store the result if not using it
        await AGTHallService.setUserInterfaceStyle(userId, newStyle);
      } catch (error) {
        console.error('[FRONTEND] Error saving interface style to API:', error.message);
      }
    } else {
      console.warn('[FRONTEND] No userId available, style preference won\'t persist');
    }
  }, [userId]);

  const handleMessageSubmit = useCallback((e) => {
    e?.preventDefault();
    if (state.input.trim() && !isWaiting) {
      // Guardar el mensaje antes de limpiar
      const messageText = state.input;

      // Limpiar el input inmediatamente
      updateState({
        input: '',
        showTrending: false,
        showSuggestions: false,
      });

      // Enviar el mensaje con la estructura correcta que espera ChatViewNest
      const messageToSend = {
        text: messageText,
        source: 'homeInput'
      };
      onSendMessage(messageToSend);
    }
  }, [state.input, isWaiting, onSendMessage, updateState]);

  // Modificar handleClickAway para que solo afecte a las sugerencias
  const handleClickAway = useCallback(() => {
    // Solo ocultar sugerencias/trending si están visibles
    if (state.showTrending || state.showSuggestions) {
      updateState({ 
        showTrending: false, 
        showSuggestions: false 
      });
    }
  }, [updateState, state.showTrending, state.showSuggestions]);

  // Obtener el componente de estilo correspondiente
  const styleComponent = getStyleComponent(interfaceStyle);
  
  // Obtener estilos específicos para el contenedor
  const containerStyleOverrides = styleComponent.getContainerStyles(theme);

  // Mostrar un estado de carga mientras se obtiene el estilo
  if (isLoadingStyle && userId) {
    return (
      <CenteredContainer>
        <Box display="flex" justifyContent="center" alignItems="center" height="100%">
          <AppleSpinner sizeDesktop={60} sizeMobile={40} />
        </Box>
      </CenteredContainer>
    );
  }

  // Preparar las props para pasar al layout
  const layoutProps = {
    state,
    isWaiting,
    inputRef,
    handleMessageSubmit,
    updateState,
    debouncedUpdateSuggestions,
    waitingMessage,
    AGT_CARDS,
    PROMPT_CARDS,
    randomPromptCards,
    handleCardClick,
    isPro,
    selectedCard,
    handleCustomizeClick,
    interfaceStyle,
    selectedApps,
    onSuggestionClick: handleCardClick, // ✅ CORREGIDO: Asegurar que se pasa correctamente
    t // Pasar la función de traducción
  };

  return (
    <CenteredContainer sx={containerStyleOverrides}>
      <ClickAwayListener 
        onClickAway={handleClickAway}
        // Solo activar el ClickAwayListener cuando realmente lo necesitamos
        mouseEvent={state.showSuggestions || state.showTrending ? 'onClick' : false}
        touchEvent={state.showSuggestions || state.showTrending ? 'onTouchEnd' : false}
      >
        <Box width="100%" position="relative">
          {/* Renderizar el layout correspondiente al estilo seleccionado */}
          {styleComponent.renderLayout(layoutProps)}
          
          {state.showTrending && !state.input && !isWaiting && (
            <Box position="absolute" width="100%" zIndex={1}>
              <TrendingSuggestions onSuggestionClick={handleCardClick} />
            </Box>
          )}
        </Box>
      </ClickAwayListener>
      
      {/* Usar el componente CustomizeModal en lugar del código embebido */}
      <CustomizeModal
        open={customizeDialogOpen}
        onClose={handleCloseCustomizeDialog}
        currentStyle={interfaceStyle}
        onStyleChange={handleStyleChange}
        selectedApps={selectedApps}
        userId={userId}
      />
      
      <FooterLinks />
    </CenteredContainer>
  );
};

HomeInput.propTypes = {
  onSendMessage: PropTypes.func.isRequired,
  onOpenAGTPromo: PropTypes.func.isRequired,
  isPro: PropTypes.bool,
  userId: PropTypes.string
};

FooterLinks.propTypes = {};
HelpMenu.propTypes = {};

export default React.memo(HomeInput);